import { ChromeTooltipSide } from './ChromeTooltipWidget';

/**
 * Asserts that a given value is of type `never`, which is the type left after all other cases have been removed, see
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html.
 * @param x - The value that should never occur.
 */
export function assertNever(x: never): never {
  throw new Error('Unexpected object: ' + x);
}

/**
 * Number of sides of a rectangle.
 */
export const NumberOfSides = 4;

/**
 * Checks if a given side is horizontal, i.e., left or right.
 * @param side - The side to check.
 */
export function isHorizontalSide(side: ChromeTooltipSide): boolean {
  return side === 'Left' || side === 'Right';
}

/**
 * Checks if a given side is vertical, i.e., top or bottom.
 * @param side - The side to check.
 */
export function isVerticalSide(side: ChromeTooltipSide): boolean {
  return !isHorizontalSide(side);
}

/**
 * Returns the side next to a given side in the clockwise direction.
 * @param side - The previous side.
 */
export function getNextSideClockwise(side: ChromeTooltipSide): ChromeTooltipSide {
  switch (side) {
    case 'Top':    return 'Right';
    case 'Right':  return 'Bottom';
    case 'Bottom': return 'Left';
    case 'Left':   return 'Top';
    default:       return assertNever(side);
  }
}

/**
 * Returns the side next to a given side in the counter-clockwise direction.
 * @param side - The previous side.
 */
export function getNextSideCounterClockwise(side: ChromeTooltipSide): ChromeTooltipSide {
  switch (side) {
    case 'Top':    return 'Left';
    case 'Left':   return 'Bottom';
    case 'Bottom': return 'Right';
    case 'Right':  return 'Top';
    default:       return assertNever(side);
  }
}

/**
 * Interval of real numbers.
 */
export abstract class Interval {
  /**
   * Constructs a new interval.
   * @param min - Greatest lower bound of interval.
   * @param max - Least upper bound of interval.
   */
  public constructor(public readonly min: number, public readonly max: number) {
    if (max < min) {
      throw new Error('max less than min');
    }
  }

  public abstract containsNumber(n: number): boolean;

  public abstract containsInterval(i: Interval): boolean;

  public equals(i: Interval): boolean {
    return this.containsInterval(i) && i.containsInterval(this);
  }
}

/**
 * Closed interval of real numbers.
 */
export class ClosedInterval extends Interval {
  public containsNumber(n: number): boolean {
    return this.min <= n && n <= this.max;
  }

  public containsInterval(i: Interval): boolean {
    return this.min <= i.min && i.max <= this.max;
  }
}

/**
 * Open interval of real numbers.
 */
export class OpenInterval extends Interval {
  public containsNumber(n: number): boolean {
    return this.min < n && n < this.max;
  }

  public containsInterval(i: Interval): boolean {
    let p = i.containsNumber(i.min) ? this.min < i.min : this.min <= i.min;
    let q = i.containsNumber(i.max) ? i.max < this.max : i.max <= this.max;
    return p && q;
  }
}

/**
 * Rectangle in the plane.
 */
export abstract class Rectangle {
  protected readonly abstract xInterval: Interval;

  protected readonly abstract yInterval: Interval;

  public containsPoint(x: number, y: number): boolean {
    return this.xInterval.containsNumber(x) && this.yInterval.containsNumber(y);
  }

  public containsRectangle(r: Rectangle): boolean {
    return this.xInterval.containsInterval(r.xInterval) && this.yInterval.containsInterval(r.yInterval);
  }

  public equals(r: Rectangle): boolean {
    return this.containsRectangle(r) && r.containsRectangle(this);
  }
}

/**
 * Closed rectangle in the plane.
 */
export class ClosedRectangle extends Rectangle {
  protected readonly xInterval: ClosedInterval;

  protected readonly yInterval: ClosedInterval;

  public constructor(minX: number, maxX: number, minY: number, maxY: number) {
    super();
    this.xInterval = new ClosedInterval(minX, maxX);
    this.yInterval = new ClosedInterval(minY, maxY);
  }
}

/**
 * Checks if a given outer rectangle contains a given inner rectangle.
 * @param clientRect1 - The outer rectangle.
 * @param clientRect2 - The inner rectangle.
 */
export function containsClientRect(clientRect1: ClientRect, clientRect2: ClientRect): boolean {
  let rect1 = clientRectToClosedRectangle(clientRect1);
  let rect2 = clientRectToClosedRectangle(clientRect2);
  return rect1.containsRectangle(rect2);
}

function clientRectToClosedRectangle({ left, right, top, bottom }: ClientRect): ClosedRectangle {
  return new ClosedRectangle(left, right, top, bottom);
}
